'use client'

import { IconChevronDown, IconChevronUp } from '@macarongfactory/web-icons'
import { Text } from '@macarongfactory/web-ui'
import { useMemo, useRef, useState } from 'react'

import { buildFaqItems, type IFaqEntry } from '@/features/seoPlaceDetail/lib'
import { useImpressionLog } from '@/features/seoPlaceDetail/model'
import {
  type IProductGroup,
  type ISeoPlace,
} from '@/features/seoPlaceDetail/model'
import { logEvent } from '@/shared/external/firebase/analytics'
import { CODE, EVENT_NAMES, LABELS, TYPE } from '@/shared/lib'
import { cn } from '@/shared/lib/cn'

import PlaceSectionDivider from '../PlaceSectionDivider/PlaceSectionDivider'

interface IPlaceFaqProps {
  place: ISeoPlace
  productGroups: IProductGroup[]
}

// 명세상 질문 라벨은 앞 40자만 적재
const FAQ_LABEL_MAX_LENGTH = 40

const SECTION_IMPRESSION_PARAMS = {
  code: CODE.COMMON_WEB_PLACE.FAQ,
  type: TYPE.SECTION,
  label: LABELS.COMMON_WEB_PLACE.FAQ,
}

const PlaceFaq = ({ place, productGroups }: IPlaceFaqProps) => {
  const [expandedIndices, setExpandedIndices] = useState<Set<number>>(new Set())
  const faqItems: IFaqEntry[] = useMemo(
    () => buildFaqItems(place, productGroups),
    [place, productGroups]
  )
  const sectionRef = useRef<HTMLElement | null>(null)
  useImpressionLog({
    ref: sectionRef,
    eventName: EVENT_NAMES.COMMON_WEB_PLACE.SECTION.SHOW,
    params: SECTION_IMPRESSION_PARAMS,
  })

  const toggle = (index: number, question: string) => {
    logEvent(EVENT_NAMES.COMMON_WEB_PLACE.ITEM.CLICK, {
      code: CODE.COMMON_WEB_PLACE.FAQ,
      type: TYPE.ITEM,
      label: question.slice(0, FAQ_LABEL_MAX_LENGTH),
    })
    setExpandedIndices(prev => {
      const next = new Set(prev)
      if (next.has(index)) next.delete(index)
      else next.add(index)
      return next
    })
  }

  return (
    <>
      <PlaceSectionDivider />
      <section
        ref={sectionRef}
        aria-label='자주 묻는 질문'
        className='flex flex-col gap-dimension-x4 bg-background-surface-default py-dimension-x6'
      >
        <div className='px-dimension-x5'>
          <Text
            as='h2'
            variant='title-1-semibold'
            color='text-foreground-neutral-strong'
          >
            자주 묻는 질문
          </Text>
        </div>

        <div className='flex flex-col'>
          {faqItems.map((item, index) => (
            <FaqItem
              key={item.question}
              item={item}
              isExpanded={expandedIndices.has(index)}
              onToggle={() => toggle(index, item.question)}
            />
          ))}
        </div>
      </section>
    </>
  )
}

interface IFaqItemProps {
  item: IFaqEntry
  isExpanded: boolean
  onToggle: () => void
}

const FaqItem = ({ item, isExpanded, onToggle }: IFaqItemProps) => {
  const ChevronIcon = isExpanded ? IconChevronUp : IconChevronDown
  const itemRef = useRef<HTMLDivElement | null>(null)
  const impressionParams = useMemo(
    () => ({
      code: CODE.COMMON_WEB_PLACE.FAQ,
      type: TYPE.ITEM,
      label: item.question.slice(0, FAQ_LABEL_MAX_LENGTH),
    }),
    [item.question]
  )
  useImpressionLog({
    ref: itemRef,
    eventName: EVENT_NAMES.COMMON_WEB_PLACE.ITEM.SHOW,
    params: impressionParams,
  })

  return (
    <div
      ref={itemRef}
      className={cn(
        'flex flex-col',
        isExpanded && 'bg-background-neutral-weak'
      )}
    >
      <button
        type='button'
        onClick={onToggle}
        className='flex w-full items-center gap-dimension-x2 px-dimension-x5 py-dimension-x3-5 text-left'
        aria-expanded={isExpanded}
      >
        <IconQQuestion />

        <Text
          variant={isExpanded ? 'title-2-semibold' : 'title-2-medium'}
          color='text-foreground-neutral-strong'
          className='min-w-0 flex-1 whitespace-pre-line'
        >
          {item.question}
        </Text>
        <ChevronIcon
          width={24}
          height={24}
          className='shrink-0 text-foreground-neutral-assistive'
        />
      </button>

      {isExpanded && (
        <div className='pb-dimension-x3-5 pl-[56px] pr-dimension-x5'>
          <Text
            variant='title-2-medium'
            color='text-foreground-neutral-strong'
            className='whitespace-pre-line'
          >
            {item.answer}
          </Text>
        </div>
      )}
    </div>
  )
}

// @macarongfactory/web-icons에 동등한 Q 글자 아이콘이 없어 인라인 SVG 사용. DS 추가 시 교체.
const IconQQuestion = () => (
  <svg
    width='24'
    height='24'
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    aria-hidden='true'
    className='shrink-0'
  >
    <path
      d='M18.52 11.704C18.52 7.832 15.64 5 11.752 5C7.864 5 5 7.832 5 11.704C5 15.576 7.88 18.408 11.768 18.408C12.984 18.408 14.104 18.136 15.064 17.624L16.904 19.944L18.216 18.952L16.424 16.68C17.736 15.48 18.52 13.736 18.52 11.704ZM11.768 16.872C8.84 16.872 6.68 14.712 6.68 11.704C6.68 8.68 8.824 6.536 11.752 6.536C14.68 6.536 16.84 8.68 16.84 11.704C16.84 13.192 16.312 14.488 15.416 15.4L13.048 12.408L11.736 13.416L14.072 16.36C13.384 16.696 12.616 16.872 11.768 16.872Z'
      fill='#00AFFF'
    />
  </svg>
)

export default PlaceFaq
